import React from "react";
import { Link } from "react-router-dom";
import { Building2, Package, ShoppingBag, Store, Bike, Search, CreditCard, Clock, MapPin, ArrowRight, ExternalLink } from "lucide-react";
import { MarketingShell } from "@/components/marketing/MarketingShell";
import { Seo } from "@/components/marketing/Seo";
import { SITE, orgSchema, productSchema, softwareAppSchema, breadcrumbSchema } from "@/lib/seo";

const FEATURES = [
  { icon: ShoppingBag, t: "Online food ordering" },
  { icon: Search, t: "Restaurant discovery" },
  { icon: Package, t: "Digital menus" },
  { icon: CreditCard, t: "Online payments" },
  { icon: Clock, t: "Order tracking" },
  { icon: MapPin, t: "Live delivery partner tracking" },
  { icon: Store, t: "Restaurant management" },
  { icon: Bike, t: "Delivery partner tools" },
];

export default function AboutBiteGoFood() {
  return (
    <MarketingShell>
      <Seo
        title="What is BiteGoFood? Food Delivery by Vijay Software Solutions"
        description="BiteGoFood (also known as BiteGo Food) is a food delivery platform by Vijay Software Solutions Pvt Ltd (VS-Solutions). Learn what BiteGoFood is, who operates it and how it works."
        path="/about/bitegofood"
        schemas={[orgSchema(), productSchema(), softwareAppSchema(),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "About", path: "/about" }, { name: "BiteGoFood", path: "/about/bitegofood" }])]}
      />
      <article className="max-w-3xl mx-auto px-4 py-14">
        <nav className="text-xs text-ink-600 mb-4" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-coral">Home</Link> › <Link to="/about" className="hover:text-coral">About</Link> › <span className="text-ink">BiteGoFood</span>
        </nav>

        <h1 className="font-display font-extrabold text-ink text-3xl sm:text-4xl">What is BiteGoFood?</h1>
        <p className="text-coral font-bold mt-2">Food Near You, Faster Than Ever.</p>
        <p className="text-ink-600 mt-4 text-lg">
          <strong>BiteGoFood</strong> (also written as <strong>BiteGo Food</strong>, and sometimes shortened to <strong>BiteGo</strong>) is a modern
          food delivery platform by <strong>{SITE.company}</strong>. It connects customers, restaurants and delivery
          partners on one simple platform to make ordering food online fast and reliable.
        </p>

        <h2 className="font-display font-bold text-ink text-xl mt-8">Who owns and operates BiteGoFood?</h2>
        <p className="text-ink-600 mt-2">
          BiteGoFood is a product of <strong>{SITE.company}</strong> (also known as <strong>VS-Solutions</strong> /
          <strong> Vijay Software Solutions</strong>), a technology company. BiteGoFood is developed and operated by the
          company — they are not separate, unrelated businesses. The company is led by Founder &amp; CEO <strong>{SITE.founder}</strong>.
        </p>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <div className="rounded-2xl border border-border p-5">
            <div className="flex items-center gap-2 text-coral"><Package size={18} /><span className="font-bold text-ink">Product / brand</span></div>
            <p className="font-display font-extrabold text-ink text-lg mt-2">BiteGoFood</p>
            <p className="text-ink-600 text-sm">Food delivery platform</p>
            <a href={SITE.url} className="text-coral font-semibold text-sm mt-2 inline-flex items-center gap-1">bitego.org <ExternalLink size={13} /></a>
          </div>
          <div className="rounded-2xl border border-border p-5">
            <div className="flex items-center gap-2 text-coral"><Building2 size={18} /><span className="font-bold text-ink">Company</span></div>
            <p className="font-display font-extrabold text-ink text-lg mt-2">{SITE.company}</p>
            <p className="text-ink-600 text-sm">Technology company (VS-Solutions)</p>
            <a href={SITE.companyUrl} target="_blank" rel="noopener noreferrer" className="text-coral font-semibold text-sm mt-2 inline-flex items-center gap-1">vs-solutions.in <ExternalLink size={13} /></a>
          </div>
        </div>

        <p className="text-ink-600 mt-4 text-sm">
          <strong>Official websites:</strong> the main BiteGoFood product &amp; app website is{" "}
          <a href={SITE.url} className="text-coral font-semibold">bitego.org</a>, and the official BiteGoFood advertising &amp;
          promotions website is{" "}
          <a href={SITE.adsUrl} target="_blank" rel="noopener noreferrer" className="text-coral font-semibold">ads-bitego.org</a>.
        </p>

        <h2 className="font-display font-bold text-ink text-xl mt-8">What does BiteGoFood do?</h2>
        <p className="text-ink-600 mt-2">BiteGoFood provides an online food ordering and delivery experience for three types of users:</p>
        <ul className="mt-3 space-y-2 text-ink-600">
          <li className="flex gap-2"><ShoppingBag size={18} className="text-coral shrink-0" /> <span><strong className="text-ink">Customers</strong> discover restaurants, order food online, pay securely and track their order live.</span></li>
          <li className="flex gap-2"><Store size={18} className="text-coral shrink-0" /> <span><strong className="text-ink">Restaurants</strong> list menus, receive and manage orders from a dedicated dashboard.</span></li>
          <li className="flex gap-2"><Bike size={18} className="text-coral shrink-0" /> <span><strong className="text-ink">Delivery partners</strong> accept and deliver orders with live location sharing.</span></li>
        </ul>

        <h2 className="font-display font-bold text-ink text-xl mt-8">Key features</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
          {FEATURES.map((f) => (
            <div key={f.t} className="rounded-xl border border-border p-3 text-center">
              <f.icon size={20} className="text-coral mx-auto" />
              <div className="text-xs text-ink-600 mt-1.5">{f.t}</div>
            </div>
          ))}
        </div>

        <h2 className="font-display font-bold text-ink text-xl mt-8">Where is BiteGoFood available?</h2>
        <p className="text-ink-600 mt-2">
          BiteGoFood is built to connect customers, restaurants and delivery partners across <strong>India</strong> as the
          platform expands into new cities and service areas. Delivery availability depends on the active service areas
          shown on the platform — more locations are added as BiteGoFood grows.
        </p>

        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/signup" className="bg-coral text-white font-bold px-5 py-3 rounded-full hover:bg-coral-hover transition-colors">Order food</Link>
          <Link to="/about" className="inline-flex items-center gap-2 text-coral font-bold px-5 py-3">About the company <ArrowRight size={15} /></Link>
        </div>
      </article>
    </MarketingShell>
  );
}
